import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { FiCheck, FiCopy, FiExternalLink, FiEye, FiX } from "react-icons/fi";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import DocumentViewDialog from "@/components/patient/documents/document-view-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { useSpakinectPortalUrl } from "@/lib/spakinect-utils";
import { toast } from "@/lib/use-toast";
import type { DocumentSummary } from "@/types/document";
import { GFEStatus } from "@/types/enums/spakinect";
import type { GFEDetail } from "@/types/spakinect";
import { useCancelGFEMutation } from "@/api/spakinect/gfe";
interface GFEActionsMenuProps {
  gfe: GFEDetail;
  children: React.ReactNode;
  // History only: the clinic no longer orders through SpaKinect, so hide the
  // actions that act on the visit (open/copy its link, cancel it).
  readOnly?: boolean;
}
const canCancelGFE = (status: GFEStatus | null): boolean => {
  // Only "Processing" or "Pending Review" statuses can be cancelled per Spakinect API
  return status === GFEStatus.PROCESSING || status === GFEStatus.PENDING_REVIEW;
};
export function GFEActionsMenu(t0) {
  const $ = _c(40);
  const {
    gfe,
    children,
    readOnly: t1
  } = t0;
  const readOnly = t1 === undefined ? false : t1;
  const getSpakinectPortalUrl = useSpakinectPortalUrl();
  const [gfeToCancel, setGfeToCancel] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const cancelGFEMutation = useCancelGFEMutation();
  let t2;
  if ($[0] !== gfe.visitLink) {
    t2 = e => {
      e.preventDefault();
      e.stopPropagation();
      if (gfe.visitLink) {
        navigator.clipboard.writeText(gfe.visitLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast({
          title: "Link Copied",
          description: "Visit link copied to clipboard."
        });
      }
    };
    $[0] = gfe.visitLink;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleCopyLink = t2;
  let t3;
  if ($[2] !== children) {
    t3 = <DropdownMenuTrigger asChild={true}>{children}</DropdownMenuTrigger>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== gfe.document) {
    t4 = gfe.document && <DropdownMenuItem onClick={() => {
      setSelectedDocument(gfe.document);
      setIsDocumentViewerOpen(true);
    }}><FiEye className="mr-2 h-4 w-4" />View GFE Document</DropdownMenuItem>;
    $[4] = gfe.document;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== getSpakinectPortalUrl || $[7] !== gfe.spakinectGfeId) {
    t5 = () => window.open(getSpakinectPortalUrl(gfe.spakinectGfeId), "_blank", "noopener,noreferrer");
    $[6] = getSpakinectPortalUrl;
    $[7] = gfe.spakinectGfeId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <FiExternalLink className="mr-2 h-4 w-4" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <DropdownMenuItem onClick={t5}>{t6}View GFE Details</DropdownMenuItem>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== copied || $[13] !== gfe.status || $[14] !== gfe.visitLink || $[15] !== handleCopyLink || $[16] !== readOnly) {
    t8 = !readOnly && gfe.visitLink && gfe.status === GFEStatus.INTAKE && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => {
        if (gfe.visitLink) {
          window.open(gfe.visitLink, "_blank", "noopener,noreferrer");
        }
      }}><FiExternalLink className="mr-2 h-4 w-4" />Open Visit Link</DropdownMenuItem><DropdownMenuItem onClick={handleCopyLink}>{copied ? <FiCheck className="mr-2 h-4 w-4 text-status-success-text" /> : <FiCopy className="mr-2 h-4 w-4" />}{copied ? <span>{"Copied!"}</span> : <span>{"Copy Visit Link"}</span>}</DropdownMenuItem></>;
    $[12] = copied;
    $[13] = gfe.status;
    $[14] = gfe.visitLink;
    $[15] = handleCopyLink;
    $[16] = readOnly;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== cancelGFEMutation || $[19] !== gfe.document || $[20] !== gfe.id || $[21] !== gfe.patientId || $[22] !== gfe.status || $[23] !== gfeToCancel || $[24] !== readOnly) {
    t9 = !readOnly && canCancelGFE(gfe.status) && <>{gfe.document && <DropdownMenuSeparator />}<DeleteConfirmationModal isOpen={gfeToCancel === gfe.id} setIsOpen={open => !open && setGfeToCancel(null)} onConfirm={() => {
        if (gfe.id) {
          cancelGFEMutation.mutate({
            gfeId: gfe.id,
            patientId: gfe.patientId
          }, {
            onSuccess: () => {
              setGfeToCancel(null);
            }
          });
        }
      }} title="Cancel Good Faith Exam" subtitle="Are you sure you want to cancel this Good Faith Exam appointment? This action cannot be undone." confirmText="Cancel Appointment" isLoading={cancelGFEMutation.isPending}><DropdownMenuItem onSelect={e_0 => {
          e_0.preventDefault();
          setGfeToCancel(gfe.id);
        }} className="text-status-error-text"><FiX className="mr-2 h-4 w-4" />Cancel GFE</DropdownMenuItem></DeleteConfirmationModal></>;
    $[18] = cancelGFEMutation;
    $[19] = gfe.document;
    $[20] = gfe.id;
    $[21] = gfe.patientId;
    $[22] = gfe.status;
    $[23] = gfeToCancel;
    $[24] = readOnly;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t4 || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = <DropdownMenuContent align="end">{t4}{t7}{t8}{t9}</DropdownMenuContent>;
    $[26] = t4;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t3) {
    t11 = <DropdownMenu>{t3}{t10}</DropdownMenu>;
    $[31] = t10;
    $[32] = t3;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== isDocumentViewerOpen || $[35] !== selectedDocument) {
    t12 = <DocumentViewDialog isOpen={isDocumentViewerOpen} onOpenChange={setIsDocumentViewerOpen} document={selectedDocument} />;
    $[34] = isDocumentViewerOpen;
    $[35] = selectedDocument;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t11 || $[38] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
}